/**
 * Compact text form of a grid of material ids, to carry a map inside a scenario (and so in replay logs).
 * Runs of equal cells: [id byte, run length as LEB128 varint]..., then base64. Pure integer code, no DOM.
 */

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_INDEX = new Int16Array(128).fill(-1);
for (let i = 0; i < B64.length; i++) B64_INDEX[B64.charCodeAt(i)] = i;

function toBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
    out += i + 1 < bytes.length ? B64[(n >> 6) & 63] : '=';
    out += i + 2 < bytes.length ? B64[n & 63] : '=';
  }
  return out;
}

function fromBase64(text: string): Uint8Array {
  const clean = text.replace(/=+$/, '');
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let o = 0;
  for (let i = 0; i < clean.length; i += 4) {
    let n = 0;
    for (let j = 0; j < 4; j++) {
      const c = clean.charCodeAt(i + j);
      const v = i + j < clean.length ? (c < 128 ? B64_INDEX[c] : -1) : 0;
      if (v < 0) throw new Error('grid: invalid base64');
      n = (n << 6) | v;
    }
    if (o < out.length) out[o++] = (n >> 16) & 0xff;
    if (o < out.length) out[o++] = (n >> 8) & 0xff;
    if (o < out.length) out[o++] = n & 0xff;
  }
  return out;
}

export function encodeGrid(cells: Uint8Array): string {
  const bytes: number[] = [];
  for (let i = 0; i < cells.length;) {
    const v = cells[i];
    let run = 1;
    while (i + run < cells.length && cells[i + run] === v) run++;
    bytes.push(v);
    for (let r = run; ; r = Math.floor(r / 128)) {
      if (r < 128) { bytes.push(r); break; }
      bytes.push((r % 128) | 128);
    }
    i += run;
  }
  return toBase64(Uint8Array.from(bytes));
}

/** Decodes exactly `length` cells; throws if the text is malformed or does not describe that many. */
export function decodeGrid(text: string, length: number): Uint8Array {
  const bytes = fromBase64(text);
  const cells = new Uint8Array(length);
  let o = 0;
  let i = 0;
  while (i < bytes.length) {
    const v = bytes[i++];
    let run = 0;
    let scale = 1;
    for (;;) {
      if (i >= bytes.length) throw new Error('grid: truncated run');
      const b = bytes[i++];
      run += (b & 127) * scale;
      if (b < 128) break;
      scale *= 128;
      if (scale > 2 ** 28) throw new Error('grid: run too long');
    }
    if (run === 0 || o + run > length) throw new Error('grid: size mismatch');
    cells.fill(v, o, o + run);
    o += run;
  }
  if (o !== length) throw new Error('grid: size mismatch');
  return cells;
}
